// hooks/useGlobalRealtime.ts
// Temps réel global de la webapp (spec 2026-10-10 §3). Avant connexion : le
// sujet public `system` seul. Après connexion : `admin` (administrateur ou
// rôle de ligue), `league:<id>` pour chaque ligue de l'utilisateur (ligues
// actives + ligue choisie pour un administrateur) et `user:<uid>`. Les effets
// sur le cache sont dans lib/realtime/globalRealtimeHandlers.ts.
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useLeagues } from './useLeagues';
import { useBroadcast } from './useBroadcast';
import { useLeagueSeasonStore } from '../state/useLeagueSeasonStore';
import { isAdminOrSuperAdmin } from '../lib/auth/isSuperAdmin';
import { ADMIN_TOPIC, SYSTEM_TOPIC, canJoinAdminTopic, leagueTopicsFor, userTopic } from '../lib/realtime/topics';
import {
    ADMIN_EVENTS,
    LEAGUE_EVENTS,
    SYSTEM_EVENTS,
    USER_EVENTS,
    createGlobalRealtimeHandlers,
} from '../lib/realtime/globalRealtimeHandlers';

const SYSTEM_TOPICS: readonly string[] = [SYSTEM_TOPIC];
const ADMIN_TOPICS: readonly string[] = [ADMIN_TOPIC];
const NO_TOPICS: readonly string[] = [];

export const useGlobalRealtime = () => {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const userId = user?.id ?? null;
    const isAdmin = isAdminOrSuperAdmin(user);
    const { data: leagues } = useLeagues({ enabled: !!userId && isAdmin });
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);

    const handlers = useMemo(() => createGlobalRealtimeHandlers(queryClient), [queryClient]);
    useEffect(() => () => handlers.dispose(), [handlers]);

    const leagueTopics = useMemo(() => leagueTopicsFor(user, leagues, currentLeagueId), [user, leagues, currentLeagueId]);
    const adminTopics = userId && canJoinAdminTopic(user) ? ADMIN_TOPICS : NO_TOPICS;
    const userTopics = useMemo(() => (userId ? [userTopic(userId)] : NO_TOPICS), [userId]);

    useBroadcast({
        topics: SYSTEM_TOPICS,
        isPrivate: false,
        ownerId: null,
        events: SYSTEM_EVENTS,
        onMessage: (_topic, event, payload) => handlers.onSystemMessage(event, payload),
        onResync: () => handlers.onSystemResync(),
    });
    useBroadcast({
        topics: leagueTopics,
        isPrivate: true,
        ownerId: userId,
        events: LEAGUE_EVENTS,
        onMessage: (_topic, event, payload) => handlers.onLeagueMessage(event, payload),
        onResync: () => handlers.onLeagueResync(),
    });
    useBroadcast({
        topics: adminTopics,
        isPrivate: true,
        ownerId: userId,
        events: ADMIN_EVENTS,
        onMessage: (_topic, event, payload) => handlers.onAdminMessage(event, payload),
        onResync: () => handlers.onAdminResync(),
    });
    useBroadcast({
        topics: userTopics,
        isPrivate: true,
        ownerId: userId,
        events: USER_EVENTS,
        onMessage: (_topic, event, payload) => handlers.onUserMessage(event, payload),
        onResync: () => handlers.onUserResync(),
    });
};
